<script setup lang="ts">
import { computed, provide, reactive, ref, useSlots } from 'vue';
import { useElementSize, useWindowSize } from '@vueuse/core';
import { motion } from 'motion-v';

const container = ref<HTMLElement | null>(null);

const { width, height } = useElementSize(container);
const viewport = useWindowSize();

const radiusX = computed(() => map(viewport.width.value, 320, 1920, 60, 500));
const radiusY = computed(() => map(viewport.width.value, 320, 1920, 60, 380));

const slots = useSlots();
const slotNodes = slots.default?.() ?? [];

const positions = getEllipsePositions(
    slotNodes.length,
    radiusX.value,
    radiusY.value,
    10
);

const nodes: Node[] = slotNodes.map((node, index) => ({
    id: index,
    target: positions[index] as { x: number; y: number },
    progress: useMotionValue(0),
}));

const links = generateLinks(nodes);

const context = reactive({
    registerTag: (tagRef: HTMLElement) => {
        // Find the next available index for this tag
        const index = context.registeredTags.length;
        context.registeredTags.push(tagRef);
        return index;
    },
    registeredTags: [] as HTMLElement[],
    getNodePosition: (index: number) => nodes[index],
    getProgress: (index: number) => nodes[index]?.progress,
});

provide('context', context);
</script>

<script lang="ts">
import { useMotionValue, useTransform } from 'motion-v';
import type { MotionValue } from 'motion-v';

interface Position {
    x: number;
    y: number;
}

interface Node {
    id: number;
    target: Position;
    progress: MotionValue<number>;
}

interface Link {
    x1: MotionValue<number>;
    x2: MotionValue<number>;
    y1: MotionValue<number>;
    y2: MotionValue<number>;
}

function getEllipsePositions(
    n: number,
    radiusX: number,
    radiusY: number,
    startAngle = 0
): Position[] {
    const positions = [];
    const angleStep = (2 * Math.PI) / (n - 1);

    positions.push({
        x: 0,
        y: 0,
    });

    for (let i = 1; i < n; i++) {
        const angle = startAngle + i * angleStep;
        const x = radiusX * Math.cos(angle) + (Math.random() * -100 + 50);
        const y = radiusY * Math.sin(angle) + (Math.random() * -100 + 50);

        positions.push({ x, y });
    }

    return positions;
}

function generateLinks(nodes: Node[]): Link[] {
    const links: Link[] = [];
    const n = nodes.length;

    // Connect center with current node
    for (let i = 1; i < n; i++) {
        const current = nodes[i] as Node;

        const x1 = useTransform(current.progress, [0, 100], [0, 0], {
            clamp: false,
        });

        const x2 = useTransform(
            current.progress,
            [0, 100],
            [0, current.target.x],
            { clamp: false }
        );

        const y1 = useTransform(current.progress, [0, 100], [0, 0], {
            clamp: false,
        });

        const y2 = useTransform(
            current.progress,
            [0, 100],
            [0, current.target.y],
            { clamp: false }
        );

        links.push({
            x1,
            x2,
            y1,
            y2,
        });
    }

    // Connect all other nodes with the next
    for (let i = 1; i < n; i++) {
        const current = nodes[i] as Node;
        const next = nodes[i + 1] ?? (nodes[1] as Node); // next or first node on radius

        const x1 = useTransform(
            current.progress,
            [0, 100],
            [0, current.target.x],
            { clamp: false }
        );
        const y1 = useTransform(
            current.progress,
            [0, 100],
            [0, current.target.y],
            { clamp: false }
        );

        const x2 = useTransform(next.progress, [0, 100], [0, next.target.x], {
            clamp: false,
        });
        const y2 = useTransform(next.progress, [0, 100], [0, next.target.y], {
            clamp: false,
        });

        links.push({
            x1,
            x2,
            y1,
            y2,
        });
    }

    return links;
}

function clamp(input: number, min: number, max: number): number {
    return input < min ? min : input > max ? max : input;
}

function map(
    current: number,
    in_min: number,
    in_max: number,
    out_min: number,
    out_max: number
): number {
    const mapped: number =
        ((current - in_min) * (out_max - out_min)) / (in_max - in_min) +
        out_min;
    return clamp(mapped, out_min, out_max);
}
</script>

<template>
    <div
        class="relative w-full lg:aspect-video lg:h-auto lg:-mt-6 xl:-mt-20 2xl:mt-10"
        ref="container"
    >
        <svg
            class="absolute inset-0 hidden overflow-visible lg:block"
            :viewBox="`0 0 ${width} ${height}`"
        >
            <g :transform="`translate(${width / 2} ${height / 2})`">
                <motion.line
                    v-for="link in links"
                    :x1="link.x1"
                    :x2="link.x2"
                    :y1="link.y1"
                    :y2="link.y2"
                    stroke="var(--primary)"
                    stroke-width="2"
                    stroke-dasharray="1 4"
                    stroke-linecap="round"
                    tabindex="-1"
                />
            </g>
        </svg>
        <slot />
    </div>
</template>
